import { useMemo } from 'react';
import { Linking, Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import Markdown, { type RenderRules } from 'react-native-markdown-display';
import { useColorScheme } from 'nativewind';

type Props = {
  content: string;
  isUser: boolean;
};

const MONO_FONT = Platform.select({
  ios: 'Menlo',
  android: 'monospace',
  default: 'monospace',
});

const bubbleTextStyles = StyleSheet.create({
  userText: {
    color: '#ffffff',
    fontSize: 14,
    lineHeight: 20,
  },
});

/**
 * Detect whether dark mode is visually active.
 *
 * On web, NativeWind's JS `useColorScheme` hook can disagree with the CSS
 * `dark:` class state (e.g. system=dark but no `.dark` class on `<html>`
 * because `darkMode: 'class'` requires explicit class toggling). Checking
 * the DOM is the only reliable way to know what the CSS is actually doing.
 */
function useIsDark(): boolean {
  const { colorScheme } = useColorScheme();

  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }

  return colorScheme === 'dark';
}

function createMarkdownStyles(isDark: boolean) {
  const textColor = isDark ? '#f9fafb' : '#1f2937';
  const headingColor = isDark ? '#ffffff' : '#111827';
  const mutedColor = isDark ? '#d1d5db' : '#374151';
  const blockquoteBackground = isDark ? '#111827' : '#f9fafb';
  const blockquoteBorder = isDark ? '#4b5563' : '#d1d5db';
  const codeBackground = isDark ? '#111827' : '#f3f4f6';
  const codeBorder = isDark ? '#374151' : '#e5e7eb';
  const codeContainerBackground = isDark ? '#0f172a' : '#f8fafc';
  const ruleColor = isDark ? '#374151' : '#e5e7eb';
  const linkColor = isDark ? '#3B9062' : '#2B7A50';

  return StyleSheet.create({
    body: {
      color: textColor,
      fontSize: 14,
      lineHeight: 20,
    },
    text: {
      color: textColor,
      fontSize: 14,
      lineHeight: 20,
    },
    paragraph: {
      marginTop: 0,
      marginBottom: 10,
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    heading1: {
      flexDirection: 'row',
      color: headingColor,
      fontSize: 24,
      lineHeight: 30,
      fontWeight: '700' as const,
      marginBottom: 8,
    },
    heading2: {
      flexDirection: 'row',
      color: headingColor,
      fontSize: 20,
      lineHeight: 26,
      fontWeight: '700' as const,
      marginBottom: 6,
    },
    heading3: {
      flexDirection: 'row',
      color: headingColor,
      fontSize: 17,
      lineHeight: 24,
      fontWeight: '700' as const,
      marginBottom: 4,
    },
    strong: {
      fontWeight: '700',
      color: headingColor,
    },
    em: {
      fontStyle: 'italic',
    },
    bullet_list: {
      marginTop: 2,
      marginBottom: 10,
    },
    ordered_list: {
      marginTop: 2,
      marginBottom: 10,
    },
    list_item: {
      flexDirection: 'row',
      marginBottom: 4,
    },
    bullet_list_icon: {
      color: mutedColor,
    },
    ordered_list_icon: {
      color: mutedColor,
    },
    blockquote: {
      backgroundColor: blockquoteBackground,
      borderLeftWidth: 3,
      borderLeftColor: blockquoteBorder,
      borderRadius: 6,
      paddingHorizontal: 10,
      paddingVertical: 8,
      marginBottom: 10,
    },
    code_inline: {
      color: textColor,
      backgroundColor: codeBackground,
      borderRadius: 4,
      paddingHorizontal: 6,
      paddingVertical: 2,
      fontFamily: MONO_FONT,
      fontSize: 12,
    },
    code_block: {
      color: textColor,
      fontFamily: MONO_FONT,
      fontSize: 12,
      lineHeight: 18,
    },
    fence: {
      color: textColor,
      fontFamily: MONO_FONT,
      fontSize: 12,
      lineHeight: 18,
    },
    hr: {
      backgroundColor: ruleColor,
      height: 1,
      marginVertical: 8,
    },
    link: {
      color: linkColor,
      textDecorationLine: 'underline',
    },
    codeScrollContent: {
      flexGrow: 1,
    },
    codeContainer: {
      marginBottom: 10,
      backgroundColor: codeContainerBackground,
      borderWidth: 1,
      borderColor: codeBorder,
      borderRadius: 8,
      padding: 10,
      alignSelf: 'stretch',
    },
  });
}

function trimTrailingNewline(value: string): string {
  if (value.length > 0 && value.endsWith('\n')) {
    return value.slice(0, -1);
  }
  return value;
}

function isSafeHttpUrl(url: string): boolean {
  const lower = url.trim().toLowerCase();
  return lower.startsWith('http://') || lower.startsWith('https://');
}

export function MarkdownMessage({ content, isUser }: Props) {
  const isDark = useIsDark();
  const markdownStyles = useMemo(() => createMarkdownStyles(isDark), [isDark]);
  const markdownRules = useMemo<RenderRules>(
    () => ({
      code_block: (node, _children, _parent, styles) => (
        <ScrollView
          key={node.key}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={markdownStyles.codeScrollContent}
        >
          <View style={markdownStyles.codeContainer}>
            <Text selectable style={styles.code_block}>
              {trimTrailingNewline(node.content)}
            </Text>
          </View>
        </ScrollView>
      ),
      fence: (node, _children, _parent, styles) => (
        <ScrollView
          key={node.key}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={markdownStyles.codeScrollContent}
        >
          <View style={markdownStyles.codeContainer}>
            <Text selectable style={styles.fence}>
              {trimTrailingNewline(node.content)}
            </Text>
          </View>
        </ScrollView>
      ),
    }),
    [markdownStyles],
  );

  if (isUser) {
    return (
      <Text style={bubbleTextStyles.userText} selectable>
        {content}
      </Text>
    );
  }

  return (
    <Markdown
      style={markdownStyles}
      rules={markdownRules}
      onLinkPress={(url) => {
        if (!isSafeHttpUrl(url)) return false;
        if (Platform.OS === 'web') {
          // New tab so the link doesn't navigate the authenticated app away
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          void Linking.openURL(url).catch(() => undefined);
        }
        return false;
      }}
    >
      {content}
    </Markdown>
  );
}
